import type React from "react";
import { createContext, useContext, useId } from "react";
import { cn } from "@/lib/utils";
import { handleTabListArrowKeys } from "@/lib/tabListKeyboard";

interface TabsContextValue {
  value: string;
  onValueChange: (v: string) => void;
  idPrefix: string;
}

const TabsContext = createContext<TabsContextValue | null>(null);

export function Tabs({
  value,
  onValueChange,
  children,
  className,
}: {
  value: string;
  onValueChange: (v: string) => void;
  children: React.ReactNode;
  className?: string;
}): React.JSX.Element {
  const idPrefix = useId();
  return (
    <TabsContext.Provider value={{ value, onValueChange, idPrefix }}>
      <div className={className}>{children}</div>
    </TabsContext.Provider>
  );
}

export function TabsList({
  children,
  className,
}: {
  children: React.ReactNode;
  className?: string;
}): React.JSX.Element {
  return (
    <div role="tablist" className={cn("flex", className)}>
      {children}
    </div>
  );
}

export function TabsTrigger({
  value,
  children,
  className,
}: {
  value: string;
  children: React.ReactNode;
  className?: string;
}): React.JSX.Element {
  const ctx = useContext(TabsContext);
  const isActive = ctx?.value === value;
  return (
    <button
      type="button"
      role="tab"
      data-value={value}
      id={ctx ? `${ctx.idPrefix}-tab-${value}` : undefined}
      aria-controls={ctx ? `${ctx.idPrefix}-panel-${value}` : undefined}
      aria-selected={isActive}
      tabIndex={isActive ? 0 : -1}
      onClick={() => ctx?.onValueChange(value)}
      onKeyDown={(e) => { if (ctx) handleTabListArrowKeys(e, ctx.onValueChange); }}
      className={cn("tab-btn", isActive && "tab-btn-active", className)}
    >
      {children}
    </button>
  );
}

export function TabsContent({
  value,
  children,
  className,
}: {
  value: string;
  children: React.ReactNode;
  className?: string;
}): React.JSX.Element | null {
  const ctx = useContext(TabsContext);
  if (ctx?.value !== value) return null;
  return (
    <div
      role="tabpanel"
      id={`${ctx.idPrefix}-panel-${value}`}
      aria-labelledby={`${ctx.idPrefix}-tab-${value}`}
      tabIndex={0}
      className={className}
    >
      {children}
    </div>
  );
}
